import { createSystem } from '@iwsdk/core';
import * as THREE from 'three';
import {
  IDetectionService,
  toRenderBatch,
  type IDetectionRenderer,
  type NormalizedPoint,
  type NormalizedRect,
  type RenderBatch,
} from '@questvisionstream/client';
import { AppConfig } from '../config';
import { getServiceManager } from '../runtime';
import { poseTracking } from '../rendering/poseTracking';
import { unprojectThroughSnapshot, type CameraPoseSnapshot } from '../rendering/PoseHistory';
import { createDetectionBox, disposeTagObject } from '../rendering/TagFactory';
import { uiLog } from '../ui/uiLog';

/**
 * Host-side implementation of the library's {@link IDetectionRenderer} seam,
 * carried over from the original client onto the LiveKit path: the vision
 * server's detections arrive on the `detections` room topic (D3 - data
 * overlays, never burned into video) and are drawn as red hollow boxes with a
 * label, placed in 3D through the capture-time pose.
 *
 * This system is also the one place the shared `poseTracking` gets its
 * per-frame camera record and its latency samples - every other
 * capture-pose consumer (strokes, the training world label) reads from it.
 */
export class DetectionRenderSystem extends createSystem({}) implements IDetectionRenderer {
  private readonly boxes: THREE.Object3D[] = [];
  private unsub: (() => void) | undefined;
  private payloadsSinceLog = 0;

  override init(): void {
    const detection = getServiceManager().resolve(IDetectionService);
    this.unsub = detection.on('detections', (payload) => {
      poseTracking.latency.observe(performance.now(), payload.pts, payload.ptsClockHz);
      this.logPayload(payload);
      this.renderDetections(toRenderBatch(payload, { invertY: AppConfig.invertY }));
    });
    this.watchReferenceSpaceReset();
  }

  /** A recenter moves the reference space out from under every placed box - clear them. */
  private watchReferenceSpaceReset(): void {
    const xr = (this.world as unknown as { renderer?: { xr?: THREE.WebXRManager } })?.renderer?.xr;
    if (!xr?.addEventListener) return;
    xr.addEventListener('sessionstart', () => {
      const referenceSpace = xr.getReferenceSpace?.();
      referenceSpace?.addEventListener?.('reset', () => {
        this.clear();
        poseTracking.clear();
      });
    });
  }

  override update(): void {
    const camera = this.world?.camera;
    if (camera) poseTracking.record(camera);
  }

  renderDetections(batch: RenderBatch): void {
    // Ephemeral: redraw the current frame's boxes from scratch.
    this.clear();

    // One pose lookup per frame so all corners of all boxes share it.
    const snapshot = poseTracking.snapshotForArrival(performance.now());
    const camera = this.world?.camera ?? null;

    for (const d of batch.detections) {
      const corners = this.rectCorners(d.rect).map((p) => this.unproject(p, snapshot, camera));
      if (corners.some((c) => c === null)) continue;
      const box = createDetectionBox(corners as THREE.Vector3[], `${d.label} ${Math.round(d.conf * 100)}%`);
      this.scene.add(box);
      this.boxes.push(box);
    }
  }

  clear(): void {
    for (const box of this.boxes) {
      this.scene.remove(box);
      disposeTagObject(box);
    }
    this.boxes.length = 0;
  }

  override destroy(): void {
    this.unsub?.();
    this.clear();
    poseTracking.clear();
  }

  /** The four corners of a normalized rect: top-left, top-right, bottom-right, bottom-left. */
  private rectCorners(rect: NormalizedRect): NormalizedPoint[] {
    const { x, y, w, h } = rect;
    return [
      { x, y },
      { x: x + w, y },
      { x: x + w, y: y + h },
      { x, y: y + h },
    ];
  }

  private unproject(
    point: NormalizedPoint,
    snapshot: CameraPoseSnapshot | null,
    camera: THREE.Camera | null,
  ): THREE.Vector3 | null {
    if (snapshot) return unprojectThroughSnapshot(snapshot, point, AppConfig.placementDistanceMeters);
    if (!camera) return null;
    const ndc = new THREE.Vector3(point.x * 2 - 1, point.y * 2 - 1, 0.5);
    ndc.unproject(camera);
    const origin = new THREE.Vector3();
    camera.getWorldPosition(origin);
    const dir = ndc.sub(origin).normalize();
    return origin.add(dir.multiplyScalar(AppConfig.placementDistanceMeters));
  }

  private logPayload(payload: {
    width: number;
    height: number;
    detections: readonly { label: string; conf: number; bbox: readonly number[] }[];
  }): void {
    this.payloadsSinceLog += 1;
    if (!uiLog.allow('detections', 1000)) return;
    const rate = this.payloadsSinceLog;
    this.payloadsSinceLog = 0;
    const { width, height, detections } = payload;
    if (detections.length === 0) {
      uiLog.push(`↓ ${width}×${height} · ${rate} frames/s · nothing found`);
      return;
    }
    uiLog.push(`↓ ${width}×${height} · ${rate} frames/s · ${detections.length} found`);
    for (const d of detections) {
      uiLog.push(`   • ${d.label} ${Math.round(d.conf * 100)}% [${d.bbox.map((n) => Math.round(n)).join(', ')}]`);
    }
  }
}
